import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Snackbar from 'components/DesignSystem/Snackbar';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SelectorChip from 'components/DesignSystem/SelectorChip';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useAutomaticOptions from 'utils/hooks/flux/useAutomaticOptions';
import useAcomphMLT from 'utils/hooks/flux/automatic/useAcomphMLT';
import useFluxAutomaticFilterSelector from 'utils/hooks/flux/automatic/filterSelector';
import useSaveFluxAutomaticPreference from 'utils/hooks/flux/automatic/useSaveFluxAutomaticPreference';
import { useGetRevisionOptionsQuery } from 'services/api/products/flux';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import { automaticOptionMap } from 'helper/flux/automatic';
import {
  possibleMltOptions,
  selectFluxAutomaticControlPanelSelectedRevision,
  setRevisionDate,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';

import SpatialSubdivision from './SpatialSubdivision';
import SavedPreferences from './SavedPreferences';
import PastSimulations from './PastSimulations';
import AutomaticStudy from './AutomaticStudy';

import './index.scss';

export type FullPanelTab = 'daily' | 'monthly' | 'revision';

type SheetSection =
  | 'subdivision' | 'unit' | 'revision' | 'frequent'
  | 'preferences' | 'study' | 'acomphMlt' | 'pastSimulations' | '';

export default function FullControlPanelSheet({
  open,
  dismiss,
  tab,
}: {
  open: boolean;
  dismiss: () => void;
  tab: FullPanelTab;
}) {
  const dispatch = useDispatch();
  const [openSection, setOpenSection] = useState<SheetSection>('');

  const {
    labeledScenarios, isFetching, loadFrequentScenario, checkLabeledScenarios,
    clearStudyOptions, loadedStudyOptions,
  } = useAutomaticOptions();
  const {
    acomph, onAcomphChange, mlt, onMltChange,
  } = useAcomphMLT();
  const { currentSubOption, mltOption, setMltOption } = useFluxAutomaticFilterSelector();
  const { setShowFormModal, saveIsEnabled } = useSaveFluxAutomaticPreference();

  const selectedRevision = useSelector(selectFluxAutomaticControlPanelSelectedRevision);
  const { data: revisionData, error: revisionError } = useGetRevisionOptionsQuery(undefined, {
    skip: tab !== 'revision',
  });
  const revisionOptions = revisionData?.data ?? [];
  useHandleRequestErrors(revisionError as ResponseError);

  const toggle = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const subdivisionText = currentSubOption
    ? `${automaticOptionMap[currentSubOption.spatialSubdivision]} · ${currentSubOption.spacialSubdivisionOption.label}`
    : undefined;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="flux-automatic-full-sheet"
    >
      <div className="flux-automatic-full-sheet-body">
        <Section
          label="Subdivisão espacial"
          valueText={subdivisionText}
          open={openSection === 'subdivision'}
          onToggle={toggle('subdivision')}
        >
          <SpatialSubdivision onPicked={() => setOpenSection('')} />
        </Section>

        {/* No desktop o MLTSelector do RVX Multicenário é montado com `disabled`. */}
        {tab === 'monthly' && (
          <Section
            label="Unidade"
            valueText={mltOption.toLocaleUpperCase()}
            open={openSection === 'unit'}
            onToggle={toggle('unit')}
          >
            {possibleMltOptions.map((option) => (
              <OptionRow
                key={option}
                label={option.toLocaleUpperCase()}
                selected={option === mltOption}
                onSelect={() => { setMltOption(option); setOpenSection(''); }}
              />
            ))}
          </Section>
        )}

        {tab === 'revision' && (
          <Section
            label="Data de Revisão"
            valueText={selectedRevision || undefined}
            open={openSection === 'revision'}
            onToggle={toggle('revision')}
          >
            {revisionOptions.length === 0 && (
              <Snackbar className="mt-2">Nenhuma revisão disponível.</Snackbar>
            )}
            {revisionOptions.map((option) => (
              <OptionRow
                key={option}
                label={option}
                selected={option === selectedRevision}
                onSelect={() => { dispatch(setRevisionDate(option)); setOpenSection(''); }}
              />
            ))}
          </Section>
        )}

        <Section
          label="Cenários Frequentes"
          open={openSection === 'frequent'}
          onToggle={toggle('frequent')}
        >
          {isFetching && <Spinner animation="border" />}
          <div className="flux-automatic-full-sheet-chips">
            {!isFetching && labeledScenarios.map((scenario) => (
              <SelectorChip
                key={scenario.label}
                shape="pill"
                tone="soft"
                selected={checkLabeledScenarios(scenario)}
                onClick={() => loadFrequentScenario(scenario)}
              >
                {scenario.label}
              </SelectorChip>
            ))}
          </div>
        </Section>

        <Section
          label="Preferências Salvas"
          open={openSection === 'preferences'}
          onToggle={toggle('preferences')}
        >
          <SavedPreferences onApplied={() => setOpenSection('')} />
        </Section>

        <Section
          label="Estudo Automático"
          valueText={loadedStudyOptions.length ? `${loadedStudyOptions.length} selecionado(s)` : undefined}
          open={openSection === 'study'}
          onToggle={toggle('study')}
        >
          <AutomaticStudy />
        </Section>

        {/* Revisão é a única aba do grupo "full" sem ACOMPH/MLT — o desktop
            (ControlPanel/index.tsx) condiciona a seção a currentTab !== 'revision'. */}
        {tab !== 'revision' && (
          <Section
            label="Incluir ACOMPH/MLT"
            open={openSection === 'acomphMlt'}
            onToggle={toggle('acomphMlt')}
          >
            <OptionRow
              label="ACOMPH (ENA verificada)"
              selectionType="multiple"
              selected={acomph}
              onSelect={() => onAcomphChange({} as React.ChangeEvent<HTMLInputElement>)}
            />
            <OptionRow
              label="MLT (média mensal de referência)"
              selectionType="multiple"
              selected={mlt}
              onSelect={() => onMltChange({} as React.ChangeEvent<HTMLInputElement>)}
            />
          </Section>
        )}

        <Section
          label="Incluir Simulações antigas"
          open={openSection === 'pastSimulations'}
          onToggle={toggle('pastSimulations')}
        >
          <PastSimulations />
        </Section>
      </div>

      <div className="flux-automatic-full-sheet-footer">
        {/* Fecha o sheet antes de abrir o diálogo: o `SavePreferencesModal` é montado pela
            view (no desktop quem monta é o ControlPanel, que não existe no mobile), e
            empilhar diálogo sobre BottomSheet deixaria o de baixo por cima pelo z-index. */}
        <SheetActionButton
          variant="secondary"
          onClick={() => { dismiss(); setShowFormModal(true); }}
          disabled={!saveIsEnabled}
        >
          Salvar preferência
        </SheetActionButton>
        <SheetActionButton variant="confirm" onClick={dismiss}>
          Aplicar filtros
        </SheetActionButton>
        <SheetActionButton variant="clear" onClick={clearStudyOptions}>
          Limpar filtros
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
